import React, { useEffect, useRef, useState } from 'react';
import { Alert, Snackbar, Stack, Typography } from '@mui/material';
import { ToastCodeCaption } from './ToastCodeCaption';
import { APP_TOP_TOAST_SNACKBAR_SX } from '../utils/appSnackbarSx';
import { nextClientToastCode } from '../utils/toastCodes';
import {
  API_ERROR_TOAST_EVENT,
  DATABASE_CONNECTION_ERROR_EVENT,
  databaseConnectionToastMessage,
  type ApiErrorToastDetail,
} from '../utils/httpClientErrorMessage';

const TOAST_DEBOUNCE_MS = 8_000;
const DEFAULT_DB_TITLE = 'Database Connection Error';

export function DatabaseConnectionToast() {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(DEFAULT_DB_TITLE);
  const [message, setMessage] = useState(databaseConnectionToastMessage());
  const [traceCode, setTraceCode] = useState('');
  const [requestId, setRequestId] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const lastShownAt = useRef(0);

  useEffect(() => {
    const show = (detail: ApiErrorToastDetail, nextTitle: string, nextMessage: string) => {
      const now = Date.now();
      setTitle(nextTitle);
      setMessage(nextMessage);
      setTraceCode(nextClientToastCode());
      setRequestId(String(detail.requestId || '').trim());
      setErrorCode(String(detail.errorCode || '').trim());
      if (now - lastShownAt.current < TOAST_DEBOUNCE_MS) {
        setOpen(true);
        return;
      }
      lastShownAt.current = now;
      setOpen(true);
    };
    const onDb = (event: Event) => {
      const detail = ((event as CustomEvent<ApiErrorToastDetail>).detail || {}) as ApiErrorToastDetail;
      const next = String(detail.message || '').trim() || databaseConnectionToastMessage();
      show(detail, DEFAULT_DB_TITLE, next);
    };
    const onApi = (event: Event) => {
      const detail = ((event as CustomEvent<ApiErrorToastDetail>).detail || {}) as ApiErrorToastDetail;
      const nextTitle = String(detail.title || '').trim() || DEFAULT_DB_TITLE;
      const nextMessage = String(detail.message || '').trim();
      show(detail, nextTitle, nextMessage || nextTitle);
    };
    window.addEventListener(DATABASE_CONNECTION_ERROR_EVENT, onDb);
    window.addEventListener(API_ERROR_TOAST_EVENT, onApi);
    return () => {
      window.removeEventListener(DATABASE_CONNECTION_ERROR_EVENT, onDb);
      window.removeEventListener(API_ERROR_TOAST_EVENT, onApi);
    };
  }, []);

  const body =
    title.toLowerCase() === DEFAULT_DB_TITLE.toLowerCase()
      ? message.replace(/^Database Connection Error\.?\s*/i, '') ||
        'Unable to connect to the database. Please try again in a moment.'
      : message && message.toLowerCase() !== title.toLowerCase()
        ? message
        : '';

  return (
    <Snackbar
      open={open}
      autoHideDuration={null}
      onClose={(_, reason) => {
        if (reason === 'clickaway' || reason === 'timeout') return;
        setOpen(false);
      }}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={APP_TOP_TOAST_SNACKBAR_SX}
    >
      <Alert
        severity="error"
        variant="filled"
        onClose={() => setOpen(false)}
        sx={{ width: '100%', maxWidth: 480 }}
        data-testid="database-connection-toast"
      >
        <Stack spacing={0.25}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
            {title}
          </Typography>
          {body ? (
            <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.4 }}>
              {body}
            </Typography>
          ) : null}
          <ToastCodeCaption
            code={traceCode}
            requestId={requestId}
            errorCode={errorCode}
            copyBody={[title, body].filter(Boolean).join('\n')}
          />
        </Stack>
      </Alert>
    </Snackbar>
  );
}
